import type { Board, Cell, Coord } from './types.ts';

export function createEmptyBoard(width: number, height: number): Board {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    throw new RangeError(`kích thước bảng không hợp lệ: ${width}x${height}`);
  }
  return { width, height, cells: Array.from({ length: width * height }, () => null) };
}

export function inBounds(board: Board, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < board.width && y < board.height;
}

export function indexOf(board: Board, x: number, y: number): number {
  return y * board.width + x;
}

export function coordOf(board: Board, index: number): Coord {
  return { x: index % board.width, y: Math.floor(index / board.width) };
}

export function getCell(board: Board, x: number, y: number): Cell | null {
  if (!inBounds(board, x, y)) throw new RangeError(`ô (${x},${y}) ngoài bảng`);
  return board.cells[indexOf(board, x, y)] ?? null;
}

/** Trả bảng mới với các ô đã đặt lại. */
export function withCells(
  board: Board,
  updates: readonly { readonly x: number; readonly y: number; readonly cell: Cell | null }[],
): Board {
  const cells = board.cells.slice();
  for (const u of updates) {
    if (!inBounds(board, u.x, u.y)) throw new RangeError(`ô (${u.x},${u.y}) ngoài bảng`);
    cells[indexOf(board, u.x, u.y)] = u.cell;
  }
  return { width: board.width, height: board.height, cells };
}

export function setCell(board: Board, x: number, y: number, cell: Cell | null): Board {
  return withCells(board, [{ x, y, cell }]);
}

/** Làm rỗng các ô (consume/phá). Ô ngoài bảng → lỗi. */
export function clearCells(board: Board, coords: readonly Coord[]): Board {
  return withCells(
    board,
    coords.map((c) => ({ x: c.x, y: c.y, cell: null })),
  );
}

export function sameCoord(a: Coord, b: Coord): boolean {
  return a.x === b.x && a.y === b.y;
}

/** So sánh theo thứ tự hàng (y tăng, cùng y thì x tăng). */
export function compareRowMajor(a: Coord, b: Coord): number {
  return a.y - b.y || a.x - b.x;
}

/** Duyệt mọi ô theo thứ tự hàng. */
export function* allCoords(board: Board): Generator<Coord> {
  for (let y = 0; y < board.height; y++) {
    for (let x = 0; x < board.width; x++) yield { x, y };
  }
}

export function countCells(board: Board, pred: (cell: Cell) => boolean): number {
  let n = 0;
  for (const c of board.cells) if (c && pred(c)) n++;
  return n;
}

/** Viên bị khoá hoặc băng: không làm đầu chuỗi, không nối vào được. */
export function isBlocked(cell: Cell | null): boolean {
  if (!cell || cell.t !== 'orb' || !cell.overlay) return false;
  return cell.overlay.type === 'lock' || cell.overlay.type === 'freeze';
}

export function hasOverlay(cell: Cell | null): boolean {
  return !!cell && cell.t === 'orb' && cell.overlay !== undefined;
}
